/**
 * LO QUE NECESITA TU ATENCIÓN — una tarjeta, no una pila de barras amarillas.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ UNA SOLA TARJETA Y NO UN AVISO POR CADA COSA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Antes cada aviso era una barra amarilla a todo lo ancho. Con dos ya ocupaban
 * un tercio de la pantalla de inicio y empujaban hacia abajo las cifras del
 * día, que es a lo que se entra de verdad.
 *
 * Y pasaba lo peor que le puede pasar a un aviso: **se aprende a ignorarlo**.
 * Cuando todo es amarillo y todo grita, nada destaca — y el día que aparezca el
 * aviso que sí importa, el ojo ya pasa por encima.
 *
 * Una sola tarjeta con una fila por asunto ocupa menos, se lee de un vistazo y
 * deja claro cuántas cosas hay pendientes. El dueño lo pidió así: «haz los
 * avisos más vistosos [...] pero tampoco que sean molestos».
 *
 * ⚠️ SI NO HAY NADA PENDIENTE, ESTA TARJETA NO EXISTE. Ni un «todo en orden»
 * ni un tick verde: un sitio que siempre tiene algo puesto deja de mirarse.
 * Que aparezca es la señal.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { cx } from "@/components/ui";

export type AsuntoPendiente = {
  /** Para React, y para no repetir un asunto dos veces. */
  id: string;
  icono: ReactNode;
  titulo: ReactNode;
  detalle?: string;
  /** A dónde se va para resolverlo. */
  a: string;
  accion: string;
  /**
   * `urgente` pinta en ámbar y va primero.
   *
   * Se reserva para lo que cuesta dinero HOY —una tasa vieja hace que cobres
   * de menos en cada venta— frente a lo que solo conviene mirar, como un
   * producto agotado.
   */
  urgente?: boolean;
};

export function Atencion({ asuntos }: { asuntos: AsuntoPendiente[] }) {
  if (asuntos.length === 0) return null;

  // Lo urgente arriba. Dentro de cada grupo se respeta el orden de quien llama.
  const ordenados = [...asuntos].sort(
    (a, b) => Number(Boolean(b.urgente)) - Number(Boolean(a.urgente)),
  );
  const hayUrgente = ordenados.some((a) => a.urgente);

  return (
    <div
      className={cx(
        "overflow-hidden rounded-[var(--radius-card)] border shadow-[var(--shadow-card)]",
        hayUrgente ? "border-warning/40" : "border-line",
      )}
    >
      {/*
        La cabecera de color es lo que hace que la tarjeta se vea sin buscarla.
        Va SOLO en la cabecera y no en toda la tarjeta: un bloque entero de
        color cansa la vista y compite con las cifras del día, que son el motivo
        real de esta pantalla.
      */}
      <div
        className={cx(
          "flex items-center gap-2.5 px-4 py-3",
          hayUrgente ? "bg-warning-soft" : "bg-surface-2",
        )}
      >
        <span
          className={cx(
            "flex size-7 shrink-0 items-center justify-center rounded-full",
            hayUrgente ? "bg-warning/20 text-warning" : "bg-surface text-muted",
          )}
        >
          <AlertTriangle size={15} />
        </span>
        <p className={cx("text-sm font-semibold", hayUrgente ? "text-warning" : "text-ink")}>
          {ordenados.length === 1
            ? "Una cosa por mirar"
            : `${ordenados.length} cosas por mirar`}
        </p>
      </div>

      <div className="divide-y divide-line bg-surface">
        {ordenados.map((a) => (
          <Link
            key={a.id}
            to={a.a}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-hover"
          >
            <span
              className={cx(
                "flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]",
                a.urgente ? "bg-warning-soft text-warning" : "bg-surface-2 text-muted",
              )}
            >
              {a.icono}
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-sm text-ink">{a.titulo}</span>
              {a.detalle && (
                <span className="mt-0.5 block text-xs text-muted">{a.detalle}</span>
              )}
            </span>

            {/*
              La acción se enseña como texto y no como botón. Toda la fila ya
              es un enlace: un botón dentro de algo pulsable es un objetivo
              dentro de otro, y en el celular se falla el de fuera.
            */}
            <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-accent">
              <span className="hidden sm:inline">{a.accion}</span>
              <ChevronRight size={15} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
